import { type IconProps, Badge, Divider, GroupHeader, Icon, ListItem } from '@stone-payments/jade'
import jadeTheme from '@stone-payments/jade-design-tokens/cjs/stone'
import { useHasAuthorizedPoisAcrossProfiles } from 'dashboard-experience/shared/hooks'
import { useDigitalSettingsEnabledFeatures } from 'digital-settings/shared/hooks'
import { type MenuItem } from 'shared/types'
import { useIsWebhookSettingsEnabled } from 'webhooks/shared/hooks'

import { Box } from '~/domains/platform/design-system'
import { useDeusExMachina } from '~/domains/platform/infra/deus-ex-machina/hocs/with-deus-ex-machina'
import { type Entity } from '~/domains/platform/infra/deus-ex-machina/types'
import { profileMenuConfig } from '~/domains/platform/layout/profile-menu/menu-config'
import BlackBird from '~/domains/platform/lib/blackbird'

import analitica from '../analitica'

export function SidebarProfileMenu({ entity }: { entity: Entity }) {
  const { deusExMachina } = useDeusExMachina()
  const situation = deusExMachina?.entity?.stoneAccount?.situation
  const isOneStone = situation === 'one_stone'
  const digitalSettingsEnabledFeatures = useDigitalSettingsEnabledFeatures()
  const { isFeatureFlagEnabled: isWebhookSettingsEnabled } = useIsWebhookSettingsEnabled()
  const { hasAuthorizedPois: hasLinkPoi } = useHasAuthorizedPoisAcrossProfiles(['link'])
  const { hasAuthorizedPois: hasEcommercePoi } = useHasAuthorizedPoisAcrossProfiles(['ecommerce'])
  const profileMenuItems = profileMenuConfig(
    entity,
    digitalSettingsEnabledFeatures,
    {
      isWebhookSettingsEnabled
    },
    isOneStone,
    { hasLinkPoi, hasEcommercePoi }
  )

  const handleBack = () => {
    analitica.events.profileMenu.click()
    BlackBird.goHome()
  }

  return (
    <>
      <Box py={jadeTheme.global.space[50]}>
        <ListItem
          content={<ListItem.Content label="Voltar" />}
          leading={<Icon size="medium" use="arrow-left" />}
          onClick={handleBack}
        />
      </Box>
      <Divider />
      <Box py={jadeTheme.global.space[100]}>
        <NewProfileMenu profileMenuItems={profileMenuItems} />
      </Box>
    </>
  )
}

function NewProfileMenu({ profileMenuItems }: { profileMenuItems: Record<string, MenuItem[]> }) {
  return Object.keys(profileMenuItems).map(groupKey => (
    <>
      <GroupHeader title={groupKey.toUpperCase()} variant="overline" />
      <ListProfileMenu profileMenuItems={profileMenuItems[groupKey]} />
    </>
  ))
}

function ListProfileMenu({ profileMenuItems }: { profileMenuItems: MenuItem[] }) {
  const query = BlackBird.getQuery()
  const route = BlackBird.getPathname()

  return profileMenuItems.map(item => {
    const { active, badge, icon, isNew, label, onClick, order, path, show } = item

    if (!show) {
      return null
    }

    const activeRoute = active && active(route)

    const trailingBadge = badge ? (
      <Badge label={badge.label} variant="text" voice={badge.voice} />
    ) : isNew ? (
      <Badge label="Novo" variant="text" voice="info" />
    ) : undefined

    return (
      <ListItem
        key={`${order}-${icon}`}
        role={`menuitem`}
        aria-label={`menu-item-${icon}`}
        compact
        leading={
          <Icon
            role={`presentation`}
            size="medium"
            color={activeRoute ? 'brand' : 'medium'}
            use={icon as IconProps['use']}
          />
        }
        content={<ListItem.Content label={label} clampLine={['label']} />}
        onClick={() => {
          onClick && onClick()
          path && BlackBird.travelTo({ pathname: path, ...query })
        }}
        variant={activeRoute ? 'active' : 'neutral'}
        trailing={trailingBadge}
      />
    )
  })
}
